import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Lang } from '@/data/types';
import { KBIcon } from '@/components/kbIcons';
import { friendlyError } from '@/lib/friendlyError';
import {
  actesDpcList, actesSaveDpc, actesDeleteDpc, actesDpcFile, actesDossiers, actesUpload,
  type ActesDpc, type ActesReference, type ActesDossier, type ActesPerson,
} from '@/integrations/actesApi';
import { pillOf } from './pill';
import { ActesStack, PeopleField, peopleReader } from './ActesPerson';
import { DocViewer } from './DocViewer';
import { Combobox } from './Combobox';
import { displayDossierName } from '@/hooks/useTidyName';
import { searchTicketsSmart, type TicketMatch } from '@/integrations/hubspot';
import { prettySubject } from '@/lib/subjects';
import { fmtDay, acteDay } from './dates';
import { actesEn } from '@/lib/actesCountry';

/**
 * PIÈCES COMPLÉMENTAIRES (CNF) — what a tribunal asks for after a file has
 * been deposited, and by when.
 *
 * The deadline is the whole point: miss the "date butoir" and the file is
 * refused, so it is the one thing painted in alarm when it has passed. Tying a
 * DPC to a client's case also puts that date in the portal's own calendar and
 * warns everyone on the dossier three days out — the section is not the only
 * place somebody should be able to see it coming.
 */
type Sort = 'desc' | 'asc' | '';

/* ── LES QUATRE VUES RAPIDES DU DPC (maquette v7) ──
   Les règles ne s'inventent pas : elles se lisent sur les deux dates que la
   pièce porte déjà et sur le champ « assigné à ».
     · month — la date butoir tombe dans les 30 prochains jours ;
     · none  — aucune date butoir enregistrée ;
     · free  — personne n'est assigné (ni en principal, ni à côté) ;
     · loose — aucun dossier : ni enregistré, ni désigné par son dossier
               client. C'est la pile à rattacher à la main (le formulaire
               propose le dossier qui porte le nom du client) ;
     · all   — tout. */
type DpcSeg = 'month' | 'none' | 'free' | 'loose' | 'all';
const DPC_SEGS: { k: DpcSeg; fr: string; en: string }[] = [
  { k: 'month', fr: 'Butoir ce mois-ci', en: 'Deadline this month' },
  { k: 'none', fr: 'Sans date butoir', en: 'No deadline recorded' },
  { k: 'free', fr: 'Sans assigné', en: 'Nobody assigned' },
  { k: 'loose', fr: 'Sans dossier', en: 'No dossier' },
  { k: 'all', fr: 'Tout', en: 'All' },
];
const daysN = (iso: unknown): number | null => {
  const x = acteDay(iso);
  if (!x) return null;
  const d = new Date(`${x}T00:00:00Z`).getTime();
  return Number.isNaN(d) ? null : Math.round((d - Date.now()) / 86_400_000);
};

export function DpcPanel({ lang, reference, onOpenDossier }: {
  lang: Lang; reference: ActesReference; onOpenDossier?: (id: string) => void;
}) {
  const T = (fr: string, en: string) => (lang === 'fr' ? fr : actesEn(en));
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [assignee, setAssignee] = useState('');
  const [sort, setSort] = useState<Sort>('desc');
  const [edit, setEdit] = useState<ActesDpc | null | 'new'>(null);
  const [confirm, setConfirm] = useState<ActesDpc | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [view, setView] = useState<(() => Promise<{ url: string; name: string | null; mime?: string | null }>) | null>(null);

  const list = useQuery<ActesDpc[]>({
    queryKey: ['actes', 'dpc'],
    queryFn: ({ signal }) => actesDpcList(signal),
    staleTime: 60_000,
  });
  const dossiers = useQuery<ActesDossier[]>({
    queryKey: ['actes', 'dossiers'],
    queryFn: ({ signal }) => actesDossiers(signal),
    staleTime: 5 * 60_000,
  });

  /* ── QUI EST SUR LA DPC : TOUT LE MONDE, PAS SEULEMENT LA PREMIÈRE ──
     `assigned_to` ET `assigned_emails` (voir ActesPerson.peopleReader) : la
     colonne, le filtre « Assigné à » et la vue « Sans assigné » lisent la
     même liste. 23 DPC avaient déjà deux personnes qu'on ne voyait pas, et
     21 n'avaient que des adresses — elles comptaient « sans assigné ». */
  const who = useMemo(() => peopleReader(reference.profiles), [reference.profiles]);
  const rows = list.data ?? [];
  const dirty = !!search || !!assignee;
  const [seg, setSeg] = useState<DpcSeg>('all');
  const segOf = (r: ActesDpc): DpcSeg[] => {
    const out: DpcSeg[] = ['all'];
    const d = daysN(r.dpc_deadline_on);
    if (d === null) out.push('none'); else if (d <= 30) out.push('month');
    if (!who.people(r).length) out.push('free');
    if (!r.dossier_id && !r.case_dossier_id) out.push('loose');
    return out;
  };

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    const kept = rows.filter((r) => {
      if (assignee && !who.has(r, assignee)) return false;
      if (!q) return true;
      return [r.client_name, r.request_number, r.dossier_name].filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
    const inSeg = seg === 'all' ? kept : kept.filter((r) => segOf(r).includes(seg));
    if (!sort) return inSeg;
    /* Rows with no reception date sort LAST in both directions: an empty date
       is not "the oldest", it is "not known yet". */
    return [...inSeg].sort((a, b) => {
      const x = acteDay(a.ar_received_on); const y = acteDay(b.ar_received_on);
      if (!x && !y) return 0;
      if (!x) return 1;
      if (!y) return -1;
      return sort === 'desc' ? y.localeCompare(x) : x.localeCompare(y);
    });
  }, [rows, search, assignee, sort, seg, who]);
  /* Le nombre de chaque vue se compte AVANT la vue elle-même. */
  const segCount = useMemo(() => {
    const q = search.trim().toLowerCase();
    const pre = rows.filter((r) => {
      if (assignee && !who.has(r, assignee)) return false;
      if (!q) return true;
      return [r.client_name, r.request_number, r.dossier_name].filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
    const out: Record<DpcSeg, number> = { month: 0, none: 0, free: 0, loose: 0, all: pre.length };
    for (const r of pre) for (const k of segOf(r)) if (k !== 'all') out[k] += 1;
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, search, assignee, who]);

  const fmt = (d: unknown) => fmtDay(d, lang);
  /* Compared as a plain day, whatever shape the server sent. */
  /* « dans 3 jours », « échu · 12 j » — ce qu'on veut lire à côté d'un
     butoir, comme dans la maquette. */
  const leftWord = (n: number | null): string => {
    if (n === null) return '—';
    if (n < 0) return T(`échu · ${-n} j`, `overdue · ${-n} d`);
    if (n === 0) return T('aujourd’hui', 'today');
    if (n <= 13) return T(`dans ${n} j`, `in ${n} days`);
    if (n <= 70) return T(`dans ${Math.round(n / 7)} sem.`, `in ${Math.round(n / 7)} weeks`);
    return T(`dans ${Math.round(n / 30)} mois`, `in ${Math.round(n / 30)} months`);
  };

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true); setErr('');
    try { await fn(); await qc.invalidateQueries({ queryKey: ['actes'] }); }
    catch (e) { setErr(friendlyError(e, lang)); }
    setBusy(false);
  };

  return (
    <section className="actes-panel v7">
      {/* EL BOTÓN VA EN LA MISMA LÍNEA DEL TÍTULO.
          Estaba en una fila propia encima, y una fila con un solo botón a la
          derecha no es una fila: es un hueco. `.phead` ya tiene su `.right`
          justo para esto. */}
      <div className="phead">
        <h2>{T('Pièces complémentaires (CNF)', 'Complementary pieces (CNF)')}</h2>
        {reference.me.isStaff && (
          <div className="right">
            <button className="btn btn-p" onClick={() => setEdit('new')}>
              <KBIcon name="plus" size={13} /> {T('Nouvelle pièce complémentaire', 'New complementary piece')}
            </button>
          </div>
        )}
      </div>

      <div className="toolbar">
        <label className="srch">
          <KBIcon name="search" size={15} />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder={T('Client, n° de demande ou dossier…', 'Client, request number or dossier…')} />
          {search && <button className="x" onClick={() => setSearch('')} aria-label="clear">×</button>}
        </label>
        <span className="vr" />
        <label className="hint">
          {T('Assigné à', 'Assigned to')}
          <select className="sel" value={assignee} onChange={(e) => setAssignee(e.target.value)}>
            <option value="">{T('Tous', 'All')}</option>
            {reference.profiles.map((p) => <option key={p.id} value={p.id}>{p.full_name ?? p.email}</option>)}
          </select>
        </label>
        <label className="hint">
          {T('Tri', 'Sort')}
          <select className="sel" value={sort} onChange={(e) => setSort(e.target.value as Sort)}>
            <option value="desc">{T('Réception : plus récentes', 'Reception: newest')}</option>
            <option value="asc">{T('Réception : plus anciennes', 'Reception: oldest')}</option>
            <option value="">{T('Réception : sans tri', 'Reception: unsorted')}</option>
          </select>
        </label>
        {dirty && (
          <button className="rb" title={T('Effacer les filtres', 'Clear the filters')}
            onClick={() => { setSearch(''); setAssignee(''); }}>
            <KBIcon name="trash" size={15} />
          </button>
        )}
        <span className="res mono">{shown.length} {T('résultats', 'results')}</span>
      </div>

      {/* ── LES QUATRE VUES RAPIDES DE LA MAQUETTE ──
          Elles ne demandent rien de plus : c'est un tri de ce qui est chargé,
          sur les deux dates que la pièce porte déjà. */}
      <div className="pillrow" style={{ marginTop: -6 }}>
        <div className="seg" role="group" aria-label={T('Vues rapides', 'Quick views')}>
          {DPC_SEGS.map((sg) => (
            <button key={sg.k} aria-pressed={seg === sg.k} onClick={() => setSeg(sg.k)}>
              {T(sg.fr, sg.en)} <span className="n">{segCount[sg.k]}</span>
            </button>
          ))}
        </div>
      </div>

      {err && <p className="muted-note ni-err">⚠ {err}</p>}

      {list.isLoading ? (
        <div className="empty"><b>{T('Chargement…', 'Loading…')}</b></div>
      ) : shown.length === 0 ? (
        <div className="estate">
          <span className="ic"><KBIcon name="hourglass" size={22} /></span>
          <h3>{T('Aucune pièce complémentaire', 'No complementary piece')}</h3>
          <p>{T('Rien ne correspond à ce filtre.', 'Nothing matches this filter.')}</p>
        </div>
      ) : (
        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th className="cs" />
                <th>{T('Client', 'Client')}</th>
                <th className="hide-s">{T('Dossier', 'Dossier')}</th>
                <th>{T('N° de demande', 'Request no.')}</th>
                <th>{T('Réception AR', 'AR received')}</th>
                <th>{T('Date butoir DPC', 'DPC deadline')}</th>
                <th>{T('Statut', 'Status')}</th>
                <th>{T('Assigné à', 'Assigned to')}</th>
                <th className="r" />
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => {
                const d = daysN(r.dpc_deadline_on);
                const urg = d === null ? '' : d <= 7 ? 'now' : d <= 30 ? 'soon' : '';
                const team = who.people(r);
                return (
                  <tr key={r.id}>
                    <td className="cs"><i className={urg} /></td>
                    <td className="nm-c"><b>{r.client_name}</b></td>
                    <td className="hide-s">
                      {/* Le dossier enregistré, ou celui que son dossier
                          client désigne (même numéro HubSpot, un seul dossier
                          ouvert) — le serveur le dit dans `case_dossier_id`. */}
                      {(r.dossier_id || r.case_dossier_id) && onOpenDossier
                        ? <button type="button" className="lnk"
                            title={r.dossier_id ? undefined : T(`Par le dossier client #${r.ticket_id}`, `Via the client case #${r.ticket_id}`)}
                            onClick={() => onOpenDossier((r.dossier_id || r.case_dossier_id) as string)}>{r.dossier_name || '—'}</button>
                        : <span className="dash">{r.dossier_name || '—'}</span>}
                    </td>
                    <td>{r.request_number ? <span className="chipref">{r.request_number}</span> : <span className="dash">—</span>}</td>
                    <td className="num sub">{fmt(r.ar_received_on)}</td>
                    <td className="val">
                      {r.dpc_deadline_on ? (
                        <div className="t">
                          <b>{fmt(r.dpc_deadline_on)}</b>
                          <span className={`rem ${d !== null && d <= 7 ? 'late' : ''}`}>{leftWord(d)}</span>
                        </div>
                      ) : <span className="dash">—</span>}
                    </td>
                    <td>{r.status_label_fr
                      ? <span className={`pill ${pillOf(r.status_color)}`}>{r.status_label_fr}</span>
                      : <span className="dash">—</span>}</td>
                    <td>{team.length ? <ActesStack people={team} /> : <span className="dash">—</span>}</td>
                    <td className="r">
                      <div className="rowbtns">
                        {r.file_path && (
                          <button type="button" className="rb" title={T('Voir', 'View')} disabled={busy}
                            onClick={() => setView(() => () => actesDpcFile(r.id))}>
                            <KBIcon name="eye" size={13} />
                          </button>
                        )}
                        {reference.me.isStaff && (
                          <>
                            <button type="button" className="rb" title={T('Modifier', 'Edit')} onClick={() => setEdit(r)}>
                              <KBIcon name="edit" size={13} />
                            </button>
                            <button type="button" className="rb" title={T('Supprimer', 'Delete')} onClick={() => setConfirm(r)}>
                              <KBIcon name="trash" size={13} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {view && <DocViewer lang={lang} load={view} onClose={() => setView(null)} />}

      {edit && (
        <DpcModal
          lang={lang}
          reference={reference}
          dossiers={dossiers.data ?? []}
          initial={edit === 'new' ? null : edit}
          onClose={() => setEdit(null)}
        />
      )}

      {confirm && createPortal(
        <div className="scrim actes-skin" onClick={() => setConfirm(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className="modal-h">
              <div>
                <h3>{T('Supprimer cette pièce ?', 'Delete this piece?')}</h3>
              </div>
              <button className="rb x" onClick={() => setConfirm(null)} aria-label={T('Fermer', 'Close')}>
                <KBIcon name="x" size={14} />
              </button>
            </div>
            <div className="modal-b">
              <p className="muted-note">{T('Client', 'Client')}: <strong>{confirm.client_name}</strong></p>
            </div>
            <div className="modal-f">
              <span className="spacer" />
              <button className="btn" onClick={() => setConfirm(null)}>{T('Annuler', 'Cancel')}</button>
              <button className="btn danger" disabled={busy}
                onClick={() => void act(async () => { await actesDeleteDpc(confirm.id); setConfirm(null); })}>
                {T('Supprimer', 'Delete')}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </section>
  );
}

/* ── One complementary piece ────────────────────────────────────────────── */
export function DpcModal({ lang, reference, dossiers, initial, onClose, lockDossier }: {
  lang: Lang; reference: ActesReference; dossiers: ActesDossier[];
  initial: ActesDpc | null; onClose: () => void; lockDossier?: string;
}) {
  const T = (fr: string, en: string) => (lang === 'fr' ? fr : actesEn(en));
  const qc = useQueryClient();
  const editing = !!initial;
  const [client, setClient] = useState(initial?.client_name ?? '');
  /* Sans dossier enregistré, celui que son dossier client désigne : c'est là
     que le serveur la classera à l'enregistrement, autant le montrer. */
  const [dossierId, setDossierId] = useState(initial?.dossier_id ?? initial?.case_dossier_id ?? lockDossier ?? '');
  const [swapDossier, setSwapDossier] = useState(false);
  const onDossier = dossiers.find((d) => d.id === dossierId) ?? null;

  /* ── EL DOSSIER SE PROPONE, NO SE BUSCA ──
     De 134 DPC con fecha butoir, 6 tenían dossier: la caja «Chercher un
     dossier…» vacía no invita a nada. Con el nombre del cliente escrito, si
     hay un dossier que se llama así, se ofrece — dos como mucho, un clic. */
  const suggested = useMemo(() => {
    if (dossierId) return [];
    const fold = (v: string) => v.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const words = fold(client).split(/[^a-z0-9]+/).filter((w) => w.length >= 3);
    if (!words.length) return [];
    return dossiers
      .filter((d) => !d.closed_at)
      .map((d) => ({ d, n: words.filter((w) => fold(d.name || '').includes(w)).length }))
      .filter((x) => x.n > 0 && x.n >= Math.min(words.length, 2))
      .sort((a, b) => b.n - a.n)
      .slice(0, 2)
      .map((x) => x.d);
  }, [client, dossiers, dossierId]);

  /* ── EL CASO DEL CLIENTE ──
     Es lo que cruza la DPC con el resto del portal: con un caso, la fecha
     butoir aparece en el calendario del dossier client («DPC — nombre») y el
     recordatorio de tres días antes llega a quien lleva el caso. Si el
     dossier ya está atado a un caso, es ése y no se pregunta; si no, se
     busca por nombre o por número de HubSpot, como en el dossier. */
  const viaDossier = onDossier ? (onDossier.ticket_id || onDossier.hubspot_id || null) : null;
  const [ticketId, setTicketId] = useState(initial?.ticket_id ?? '');
  const [swapTicket, setSwapTicket] = useState(false);
  const [ticketQ, setTicketQ] = useState('');
  const [hits, setHits] = useState<TicketMatch[]>([]);
  const [reading, setReading] = useState(false);
  const [searched, setSearched] = useState(false);
  const findCase = async () => {
    const q = ticketQ.trim();
    if (q.length < 2) return;
    setReading(true); setSearched(false);
    try { setHits((await searchTicketsSmart('FR', q)).slice(0, 4)); } catch { setHits([]); }
    setReading(false); setSearched(true);
  };
  /* Escribir el nombre del cliente ya es la búsqueda: no hace falta teclearlo
     dos veces. Se rellena la caja del caso mientras no la haya tocado nadie. */
  useEffect(() => { if (!ticketId && !viaDossier && !ticketQ) setTicketQ(client); }, [client]); // eslint-disable-line react-hooks/exhaustive-deps
  const [number, setNumber] = useState(initial?.request_number ?? '');
  /* Toutes les personnes de la DPC, la principale d'abord. Une DPC qui
     existait garde les siennes (`assigned_to` + `assigned_emails`) ; une
     nouvelle commence avec la personne qui la crée, comme avant. */
  const who = useMemo(() => peopleReader(reference.profiles), [reference.profiles]);
  const [team, setTeam] = useState<ActesPerson[]>(() => (initial
    ? who.people(initial)
    : reference.profiles.filter((p) => p.id === reference.me.id)));
  const [statusId, setStatusId] = useState<number | ''>(initial?.status_id ?? '');
  const [ar, setAr] = useState(initial?.ar_received_on ?? '');
  const [deadline, setDeadline] = useState(initial?.dpc_deadline_on ?? '');
  const [completude, setCompletude] = useState(initial?.ar_completude_on ?? '');
  const [drive, setDrive] = useState(initial?.drive_url ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [file, setFile] = useState<{ path: string; name: string; mime: string } | null>(
    initial?.file_path ? { path: initial.file_path, name: initial.file_name ?? 'document', mime: '' } : null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const input = useRef<HTMLInputElement>(null);

  const save = async () => {
    if (!client.trim()) { setErr(T('Le client est obligatoire.', 'The client is required.')); return; }
    setBusy(true); setErr('');
    try {
      await actesSaveDpc(initial?.id ?? null, {
        client_name: client.trim(),
        dossier_id: dossierId || null,
        ticket_id: viaDossier ? null : (ticketId || null),
        request_number: number.trim() || null,
        /* La liste entière ; le serveur en tire `assigned_to` (la première). */
        assigned_emails: team.map((p) => p.email).filter(Boolean),
        status_id: statusId === '' ? null : statusId,
        ar_received_on: ar || null,
        dpc_deadline_on: deadline || null,
        ar_completude_on: completude || null,
        drive_url: drive.trim() || null,
        notes: notes.trim() || null,
        file_path: file?.path ?? null,
        file_name: file?.name ?? null,
        file_mime: file?.mime ?? null,
      });
      await qc.invalidateQueries({ queryKey: ['actes'] });
      onClose();
    } catch (e) { setErr(friendlyError(e, lang)); }
    setBusy(false);
  };

  return createPortal(
    <div className="scrim actes-skin" onClick={onClose}>
      <div className="modal dpc-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-h">
          <div>
            <div className="eyebrow">DPC</div>
            <h3>{editing ? T('Modifier la pièce complémentaire', 'Edit complementary piece') : T('Nouvelle pièce complémentaire', 'New complementary piece')}</h3>
          </div>
          <button className="rb x" onClick={onClose} aria-label={T('Fermer', 'Close')}>
            <KBIcon name="x" size={14} />
          </button>
        </div>
        <div className="modal-b">

          <label className="fl">
            <span>{T('Client', 'Client')}</span>
            <input value={client} onChange={(e) => setClient(e.target.value)} autoFocus />
          </label>

          {/* Opened from inside a dossier, the dossier is settled — so it is
              STATED, not asked again through a search box greyed out (which
              reads as broken, not as decided). Same rule as the demande form:
              the answer, and a "change" for when it was the wrong one. */}
          <div className="fl">
            <span>{T('Dossier', 'Dossier')}</span>
            {onDossier && !swapDossier ? (
              <div className="actes-chosen quiet">
                <KBIcon name="documents" size={13} />
                <span className="actes-list-main">
                  <strong>{displayDossierName(onDossier.name)}</strong>
                  <em>{onDossier.hubspot_id ? `#${onDossier.hubspot_id}` : T('Sans dossier client', 'No case assigned')}</em>
                </span>
                {!lockDossier && (
                  <button type="button" className="alt" onClick={() => setSwapDossier(true)}>
                    {T('changer', 'change')}
                  </button>
                )}
              </div>
            ) : (
              <>
                <Combobox lang={lang} value={dossierId || null} minChars={2}
                  onChange={(v) => { setDossierId(v === null ? '' : String(v)); setSwapDossier(false); }}
                  placeholder={T('Chercher un dossier…', 'Search a dossier…')}
                  items={dossiers.map((d) => ({ id: d.id, label: displayDossierName(d.name), sub: d.hubspot_id || null }))} />
                {swapDossier && (
                  <button type="button" className="actes-inline-cancel" onClick={() => setSwapDossier(false)}>
                    {T('garder celui d’avant', 'keep the previous one')}
                  </button>
                )}
                {suggested.length > 0 && (
                  <div className="actes-dup">
                    <span className="cap">
                      {T(suggested.length === 1 ? 'Un dossier porte ce nom' : 'Deux dossiers portent ce nom',
                        suggested.length === 1 ? 'A dossier carries this name' : 'Two dossiers carry this name')}
                    </span>
                    {suggested.map((d) => (
                      <button key={d.id} type="button" className="actes-dup-row2"
                        onClick={() => { setDossierId(d.id); setSwapDossier(false); }}>
                        <KBIcon name="documents" size={13} />
                        <span className="actes-list-main">
                          <strong>{displayDossierName(d.name)}</strong>
                          <em>
                            {d.status_label_fr || '—'}
                            {d.hubspot_id || d.ticket_id ? ` · #${d.ticket_id || d.hubspot_id}` : ''}
                            {d.lead_lawyer_name ? ` · ${d.lead_lawyer_name}` : ''}
                          </em>
                        </span>
                        <span className="actes-dup-open">{T('rattacher', 'link')}</span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="fl">
            <span>{T('Dossier client (HubSpot)', 'Client case (HubSpot)')}</span>
            {viaDossier ? (
              <div className="actes-chosen quiet">
                <KBIcon name="case" size={13} />
                <span className="actes-list-main">
                  <strong>#{viaDossier}</strong>
                  <em>{T('par le dossier — la date butoir ira à son calendrier', 'via the dossier — the deadline goes to its calendar')}</em>
                </span>
              </div>
            ) : ticketId && !swapTicket ? (
              <div className="actes-chosen quiet">
                <KBIcon name="case" size={13} />
                <span className="actes-list-main">
                  <strong>#{ticketId}</strong>
                  <em>{T('la date butoir ira au calendrier de ce dossier client', 'the deadline goes to this case’s calendar')}</em>
                </span>
                <button type="button" className="alt" onClick={() => { setSwapTicket(true); setSearched(false); }}>
                  {T('changer', 'change')}
                </button>
              </div>
            ) : (
              <>
                <div className="actes-searchline">
                  <label className="actes-find wide">
                    <KBIcon name="search" size={14} />
                    <input value={ticketQ} onChange={(e) => { setTicketQ(e.target.value); setSearched(false); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void findCase(); } }}
                      placeholder={T('Nom du client ou identifiant HubSpot…', 'Client name or HubSpot id…')} />
                    {ticketQ && <button type="button" onClick={() => { setTicketQ(''); setHits([]); setSearched(false); }} aria-label="clear">×</button>}
                  </label>
                  <button type="button" className="btn btn-sm" disabled={reading || ticketQ.trim().length < 2} onClick={() => void findCase()}>
                    {reading ? '…' : T('Chercher', 'Search')}
                  </button>
                </div>
                {hits.map((t) => (
                  <button key={t.id} type="button" className="actes-suggest-row"
                    onClick={() => { setTicketId(t.id); setSwapTicket(false); setHits([]); }}>
                    <KBIcon name="case" size={13} />
                    <span className="actes-list-main">
                      <strong>{t.client || prettySubject(t.subject)}</strong>
                      <em>{[t.service, t.stageLabel, t.ownerName].filter(Boolean).join(' · ')}</em>
                    </span>
                    <span className="actes-num">#{t.id}</span>
                    <KBIcon name="chevron-right" size={13} />
                  </button>
                ))}
                {searched && hits.length === 0 && !reading && (
                  <p className="muted-note">{T('Aucun dossier client trouvé à ce nom.', 'No case found under that name.')}</p>
                )}
                {swapTicket && ticketId && (
                  <button type="button" className="actes-inline-cancel" onClick={() => setSwapTicket(false)}>
                    {T('garder celui d’avant', 'keep the previous one')}
                  </button>
                )}
              </>
            )}
          </div>

          <div className="fgrid">
            <label className="fl">
              <span>{T('N° de demande', 'Request number')}</span>
              <input value={number} onChange={(e) => setNumber(e.target.value)} />
            </label>
            {/* Plusieurs personnes : Julien ET Hector (feedback du 13 oct. 2026). */}
            <div className="fl">
              <span>{T('Assigné à', 'Assigned to')}</span>
              <PeopleField lang={lang === 'fr' ? 'fr' : 'en'} people={reference.profiles} value={team} onChange={setTeam} />
            </div>
          </div>

          <div className="fgrid">
            <label className="fl">
              <span>{T('Réception AR', 'AR received')}</span>
              <input type="date" value={ar} onChange={(e) => setAr(e.target.value)} />
            </label>
            <label className="fl">
              <span>{T('Date butoir envoi DPC', 'DPC deadline')}</span>
              <input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} />
              <em className="actes-field-note">
                {T('Rappel à l’équipe 3 jours avant ; au calendrier du dossier client si un cas est rattaché.',
                   'Team reminder 3 days before; on the client case’s calendar when a case is linked.')}
              </em>
            </label>
          </div>

          <div className="fgrid">
            <label className="fl">
              <span>{T('AR de complétude', 'Completeness AR')}</span>
              <input type="date" value={completude} onChange={(e) => setCompletude(e.target.value)} />
            </label>
            <label className="fl">
              <span>{T('Statut', 'Status')}</span>
              <select value={statusId} onChange={(e) => setStatusId(e.target.value ? Number(e.target.value) : '')}>
                <option value="">—</option>
                {reference.dpcStatuses.map((s) => (
                  <option key={s.id} value={s.id}>{lang === 'fr' ? s.label_fr : s.label_en}</option>
                ))}
              </select>
            </label>
          </div>

          <label className="fl">
            <span>{T('Lien Drive', 'Drive link')}</span>
            <input value={drive} onChange={(e) => setDrive(e.target.value)} placeholder="https://drive.google.com/…" />
          </label>

          <div className="fl">
            <span>{T('Document', 'Document')}</span>
            <div className="actes-searchline">
              <button className="btn btn-sm btn-ghost" disabled={busy} onClick={() => input.current?.click()}>
                <KBIcon name="documents" size={13} /> {file ? file.name : T('Joindre un fichier', 'Attach a file')}
              </button>
              {file && <button className="actes-row-act" onClick={() => setFile(null)}>×</button>}
              <input ref={input} type="file" hidden accept="application/pdf,image/png,image/jpeg,image/webp"
                onChange={(e) => {
                  const f = e.target.files?.[0]; e.target.value = '';
                  if (!f) return;
                  setBusy(true);
                  actesUpload(f, '_dpc').then((up) => setFile({ path: up.path, name: up.name, mime: up.mime }))
                    .catch((x) => setErr(friendlyError(x, lang))).finally(() => setBusy(false));
                }} />
            </div>
          </div>

          <label className="fl">
            <span>{T('Notes', 'Notes')}</span>
            <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </label>

          {deadline && (
            <p className="muted-note">
              {T('La date butoir apparaîtra dans le calendrier du portail si le dossier est rattaché à un dossier client, et l’équipe sera prévenue 3 jours avant.',
                'The deadline shows in the portal calendar when the dossier is tied to a case, and the team is warned 3 days before.')}
            </p>
          )}

          {err && <p className="muted-note ni-err">⚠ {err}</p>}
        </div>
        <div className="modal-f">
          <span className="spacer" />
          <button className="btn" onClick={onClose}>{T('Annuler', 'Cancel')}</button>
          <button className="btn btn-p" disabled={busy || !client.trim()} onClick={() => void save()}>
            {busy ? '…' : editing ? T('Enregistrer', 'Save') : T('Créer', 'Create')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
